import React from 'react';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Print from '@mui/icons-material/Print';
import IconButton from '@mui/material/IconButton';
import { SampleBox } from '../db';

import { alertFullScreen } from '../alertDispatcher';
import { isMobile } from 'react-device-detect';
import { FaCheck, FaQuestion, FaUps } from 'react-icons/fa';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from './utils';
import { SettingsAccessLevel } from '../types/types';
import { MdDelete } from 'react-icons/md';
import { useAppDispatch } from '../redux/hooks';
import { updateBoxTrackingNumber } from '../redux/features/boxes/boxesSlice';
import ListItemButton from '@mui/material/ListItemButton';

interface BoxHistoryItemProps {
  box: SampleBox;
  showBoxCheckin: (box: SampleBox) => Promise<void>;
  showShippingLabelPDf(box: SampleBox): Promise<void>;
  showBoxCloseScreen: (box: SampleBox) => Promise<void>;
  accessLevel: SettingsAccessLevel;
}

const BoxHistoryItem: React.FC<BoxHistoryItemProps> = ({
  box,
  showBoxCheckin,
  showShippingLabelPDf,
  showBoxCloseScreen,
  accessLevel,
}) => {
  const appDispatch = useAppDispatch();
  const sortedMissions = box.getSampleBoxMissions().sort((a, b) => (a.last_modified > b.last_modified ? 1 : -1));

  let firstMissionName = 'No Missions';

  if (sortedMissions.length > 0 && sortedMissions[0]) {
    firstMissionName = sortedMissions[0].name || 'No Missions';
  }

  const boxMissions = box.getSampleBoxMissions().sort((a, b) => (a.last_modified > b.last_modified ? 1 : -1));

  const handleListItemClick = async () => {
    await alertFullScreen(
      `Details for ${box.uid.toUpperCase()}`,
      [
        `Created Time: <b>${box.createdTimestamp.toLocaleString()}</b>`,
        `Closed Time: <b>${box.closedTimestamp ? new Date(box.closedTimestamp).toLocaleString() : 'Not closed'}</b>`,
        `Sample Count: <b>${box.getSamples().length}</b>`,
        `Friendly ID: <b>${box.friendlyId}</b>`,
        `Lab: <b>${box.labName.replace(/"/g, '')}</b>`,
        'Missions:',
        ...box.getSampleBoxMissions().map((m) => `- ${m.name}`),
      ].join('\n'),
      ['Ok'],
    );
  };

  return (
    <ListItemButton onClick={handleListItemClick} key={box.uid} divider>
      <ListItemText
        primary={
          <Typography variant="h6">
            <b>{box.short_uid}</b>
          </Typography>
        }
        secondary={<Typography>{box.friendlyId}</Typography>}
        style={{ width: 0, margin: 0 }}
      />
      {!isMobile && (
        <>
          <ListItemText
            primary={<Typography variant="body1">{box.labName}</Typography>}
            style={{ width: 0, margin: 0 }}
          />

          <ListItemText
            primary={
              <Typography>
                <b>{firstMissionName}</b>
              </Typography>
            }
            primaryTypographyProps={{ style: { textAlign: 'left' } }}
            secondary={
              <Typography>
                {boxMissions.length > 1
                  ? `${boxMissions.length - 1} more missions, click to see all (${box.getSamples().length} Samples)`
                  : `(${box.getSamples().length} Samples total)`}
              </Typography>
            }
            secondaryTypographyProps={{ style: { textAlign: 'left' } }}
            style={{
              marginLeft: 0,
              overflow: 'hidden',
              alignContent: 'start',
              width: 400,
            }}
          />
        </>
      )}
      <ListItemText style={{ width: 20 }}>
        {!box.closed && (
          <LoadingButton
            variant="outlined"
            onClick={async () => {
              await showBoxCloseScreen(box);
            }}
          >
            <CloseIcon style={{ paddingRight: 7.5 }} /> Close
          </LoadingButton>
        )}
      </ListItemText>
      {accessLevel === 'Technician' && !isMobile && (
        <ListItemIcon
          onClick={async (e) => {
            e.stopPropagation();
            await box.updateTracking('', undefined);
            appDispatch(updateBoxTrackingNumber(box));
          }}
        >
          <IconButton disabled={!box.trackingNumber} color="secondary" size="large">
            <MdDelete />
            <FaUps fontSize="32" />
          </IconButton>
        </ListItemIcon>
      )}
      <ListItemIcon
        onClick={async (e) => {
          e.stopPropagation();
          await showShippingLabelPDf(box);
        }}
      >
        <IconButton color="secondary" size="large">
          <FaUps fontSize="32" />
          {box.trackingNumber ? <FaCheck fontSize="32" /> : <FaQuestion fontSize="32" />}
        </IconButton>
      </ListItemIcon>
      <ListItemIcon
        onClick={async (e) => {
          e.stopPropagation();
          await showBoxCheckin(box);
        }}
      >
        <IconButton color="secondary" size="large">
          <Print fontSize="large" />
        </IconButton>
      </ListItemIcon>
    </ListItemButton>
  );
};

export default BoxHistoryItem;
